'use client'

import React from 'react'

import { twMerge } from 'tailwind-merge'

import { BadgeV2 } from '../../common/BadgeV2'
import { ContentPanelV2 } from '../../common/ContentPanelV2'
import { DataBlockV2 } from '../../common/DataBlockV2'
import { FileCardV2 } from '../../common/FileCardV2'
import { PageTitleV2 } from '../../common/PageTitleV2'
import { SidebarWidgetV2 } from '../../common/SidebarWidgetV2'
import { SimilarOpportunitiesV2 } from '../../common/SimilarOpportunitiesV2'
import { TabPanelV2 } from '../../common/TabPanelV2'
import { TimelineV2 } from '../../common/TimelineV2'

// ============================================================================
// Base Skeleton Primitive
// ============================================================================

interface SkeletonProps {
  className?: string
}

const Skeleton: React.FC<SkeletonProps> = ({ className }) => (
  <div
    className={twMerge(
      'animate-pulse rounded bg-primary-200 dark:bg-primary-700',
      className,
    )}
  />
)

// ============================================================================
// Main Skeleton Component
// ============================================================================

/**
 * ContractDisplayV2Skeleton - Loading skeleton for federal contract detail page
 *
 * Composes V2 component skeletons to match the actual layout exactly,
 * minimizing layout shift when content loads.
 */
const ContractDisplayV2Skeleton: React.FC = () => {
  return (
    <>
      {/* Header Block */}
      <div className="mb-6">
        <div className="flex flex-col gap-3">
          {/* Badges Row */}
          <div className="flex items-center gap-2">
            <BadgeV2.Skeleton width="sm" />
            <BadgeV2.Skeleton width="md" />
            <BadgeV2.Skeleton width="lg" />
          </div>

          {/* Title + Actions Row */}
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
            <div className="max-w-4xl flex-1 space-y-1.5">
              {/* Title skeleton */}
              <PageTitleV2.TitleSkeleton width="w-[40rem]" />
              {/* Agency subtitle */}
              <PageTitleV2.SubtitleSkeleton width="w-64" showIcon />
              {/* Solicitation number */}
              <PageTitleV2.MetaSkeleton width="w-40" />
            </div>

            {/* Action Buttons skeleton */}
            <div className="flex shrink-0 items-center gap-2">
              <Skeleton className="h-9 w-9 rounded-lg" />
              <Skeleton className="h-9 w-28 rounded-lg" />
            </div>
          </div>
        </div>
      </div>

      {/* Data Grid - 4 columns: Deadline, Days Left, Set-Aside, Notice Type */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <DataBlockV2.Skeleton showSubValue />
        <DataBlockV2.Skeleton showSubValue />
        <DataBlockV2.Skeleton />
        <DataBlockV2.Skeleton />
      </div>

      {/* Main Layout - 12 column grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Main Content Column */}
        <div className="flex flex-col gap-4 lg:col-span-8">
          {/* Tabs skeleton */}
          <TabPanelV2.ListSkeleton tabCount={2} tabWidths={['md', 'lg']} />

          {/* Content Panel skeleton */}
          <div className="mt-2 overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900">
            <ContentPanelV2.Skeleton
              lineCount={8}
              showHeader
              showFooter
              className="border-0 shadow-none"
            />
          </div>

          {/* Divider below tabs */}
          <div className="mt-4 border-t border-primary-200 dark:border-primary-700" />

          {/* Classification Codes Section skeleton */}
          <div className="mt-3 rounded-xl border border-primary-200 bg-white p-4 shadow-sm dark:border-primary-700 dark:bg-primary-900">
            <div className="mb-3 flex items-center gap-1.5">
              <Skeleton className="h-3.5 w-3.5" />
              <Skeleton className="h-4 w-36" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-3 w-32" />
              </div>
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-3 w-28" />
              </div>
            </div>
          </div>

          {/* Documents Section skeleton */}
          <div className="mt-4 rounded-xl border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-primary-100 px-4 py-3 dark:border-primary-800">
              <div className="flex items-center gap-1.5">
                <Skeleton className="h-3.5 w-3.5" />
                <Skeleton className="h-4 w-36" />
              </div>
              <Skeleton className="h-5 w-14 rounded-full" />
            </div>
            {/* File cards */}
            <div className="space-y-2.5 p-3.5">
              <FileCardV2.Skeleton />
              <FileCardV2.Skeleton />
            </div>
          </div>

          {/* Related Opportunities Section skeleton */}
          <div className="mt-6">
            {/* Header skeleton */}
            <Skeleton className="mb-3 ml-1 h-4 w-56" />
            <SimilarOpportunitiesV2.Skeleton />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4 lg:col-span-4">
          {/* View Documents Button skeleton */}
          <div className="flex w-full items-center justify-between rounded-lg border border-primary-200 bg-white p-3.5 shadow-sm dark:border-primary-700 dark:bg-primary-900">
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <Skeleton className="h-4 w-4" />
          </div>

          {/* Key Dates Timeline Widget skeleton */}
          <SidebarWidgetV2.Skeleton headerWidth="sm">
            <TimelineV2.Skeleton nodeCount={4} />
          </SidebarWidgetV2.Skeleton>

          {/* Agency Info Widget skeleton */}
          <SidebarWidgetV2.Skeleton headerWidth="md">
            <div className="space-y-3">
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-20" />
                <Skeleton className="h-5 w-40" />
              </div>
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="h-5 w-32" />
              </div>
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-12" />
                <Skeleton className="h-5 w-28" />
              </div>
            </div>
          </SidebarWidgetV2.Skeleton>

          {/* Contact Info Widget skeleton */}
          <SidebarWidgetV2.Skeleton headerWidth="lg">
            <div className="space-y-3">
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="h-4 w-32" />
              </div>
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-12" />
                <Skeleton className="h-4 w-48" />
              </div>
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-12" />
                <Skeleton className="h-4 w-28" />
              </div>
            </div>
          </SidebarWidgetV2.Skeleton>

          {/* Place of Performance Widget skeleton */}
          <SidebarWidgetV2.Skeleton headerWidth="lg">
            <div className="space-y-1">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-24" />
            </div>
          </SidebarWidgetV2.Skeleton>

          {/* Official Sources Widget skeleton */}
          <SidebarWidgetV2.Skeleton headerWidth="md">
            <Skeleton className="h-9 w-full rounded-lg" />
          </SidebarWidgetV2.Skeleton>
        </div>
      </div>
    </>
  )
}

ContractDisplayV2Skeleton.displayName = 'ContractDisplayV2Skeleton'

export { ContractDisplayV2Skeleton }
